import React, { FC, memo } from 'react';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Box, IconButton, styled, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountMenu from './account-menu';
import { colors } from '../../features/theme/settings/common/colors';
import { HEADER_DESKTOP_PX, HEADER_MOBILE_PX, DRAWER_WIDTH } from './constants';
import { useGetTenantDataByRole } from '../../features/tenant/hooks/useGetTenantDataByRole';
import logo from '../../assets/pharma-logo.png';
import Sidebar from '../../components/sidebar';
import { SidebarItemProps } from '../../components/sidebar/sidebar-item';

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<MuiAppBarProps>(({ theme }) => ({
    minHeight: HEADER_DESKTOP_PX,
    [theme.breakpoints.down('sm')]: {
        minHeight: HEADER_MOBILE_PX,
    },
    justifyContent: 'center',
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: 'none',
    borderBottom: `1px solid ${colors.grey3}`,
}));

interface HeaderProps {
    drawerOpen: boolean;
    toggleDrawer: () => void;
    navPages: SidebarItemProps[];
    headerSize: string;
}

const Header: FC<HeaderProps> = ({ toggleDrawer, drawerOpen, navPages, headerSize }) => {
    const theme = useTheme();
    const mediaDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { data: tenant, userProfile, isPortalUser, isPlatformAdmin, userAvatar } = useGetTenantDataByRole();

    return (
        <>
            <AppBar position='fixed'>
                <Toolbar sx={{ pr: '24px' }}>
                    <Box sx={{ display: { xs: 'flex', sm: 'flex', md: 'none' } }}>
                        <IconButton color='secondary' onClick={toggleDrawer} data-testid='header-burger'>
                            {!drawerOpen ? (
                                <MenuIcon color='secondary' sx={{ cursor: 'pointer', fontSize: '32px' }} />
                            ) : (
                                <CloseIcon color='secondary' sx={{ cursor: 'pointer', fontSize: '32px' }} />
                            )}
                        </IconButton>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'none', md: 'flex' } }}>
                        <img
                            src={isPlatformAdmin ? logo : tenant?.logoDataUriContent || logo}
                            style={{ width: '180px', height: '42px', objectFit: 'contain' }}
                            alt='Tenant Logo'
                        />
                    </Box>

                    <AccountMenu
                        user={userProfile}
                        isPortalUser={isPortalUser}
                        avatar={userAvatar?.avatarDataUriContent}
                    />
                </Toolbar>
            </AppBar>
            <SwipeableDrawer
                variant={mediaDesktop ? 'persistent' : 'temporary'}
                open={mediaDesktop ? true : drawerOpen}
                onClose={toggleDrawer}
                onOpen={toggleDrawer}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <Box
                    sx={{
                        width: DRAWER_WIDTH,
                        marginTop: headerSize,
                        padding: '4px 0',
                    }}
                >
                    <Sidebar pages={navPages} closeDrawer={toggleDrawer}>
                        <Box sx={{ display: { xs: 'flex', sm: 'flex', md: 'none' }, padding: '20px' }}>
                            <img
                                src={isPlatformAdmin ? logo : tenant?.logoDataUriContent || logo}
                                style={{ width: '180px', height: '50px', objectFit: 'contain' }}
                                alt='Tenant Logo'
                            />
                        </Box>
                    </Sidebar>
                </Box>
            </SwipeableDrawer>
        </>
    );
};

export default memo(Header);
